/**
 * The bell in the top bar.
 *
 * A short list of kinds on purpose: a bell that cries wolf gets muted, and a
 * muted bell is worse than no bell at all. Opening it marks everything read,
 * because the number exists to answer "is there anything new", and a badge that
 * survives the reader looking straight at it is lying.
 *
 * Each row wears the colour of the status its news is about - teal for a
 * concept landing in strategist review, magenta for one sent to the admin's
 * final review, red for "needs revision" - the same tones as the pills, so the
 * bell reads like the board. Under the sentence: what was said, when the news
 * carries words (a comment, a mention, a revision request, a strategist's
 * note), clamped to two lines and escaped like every other piece of text
 * somebody typed; for a new concept, when it is due.
 *
 * A strategist's own agent creates concepts under her name, so a concept that
 * lands in her review "from Gabriela" would read as a message from herself; the
 * row says her agent made it instead. A note on a line of the presentation goes
 * to the deck, where the editor reads it beside that line. A clip that finished
 * or failed after its requester left the popup goes to its shot's edit mode, with
 * the reason in plain words when it failed.
 *
 * v1 is in-app only, but the same rows are what a Slack or WhatsApp adapter
 * would send, so nothing here assumes this is the only place they land.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BellIcon, CheckCheckIcon } from "lucide-react";
import {
  formatRelativeTime,
  formatShortDate,
  GENERATION_ERROR_CLASSES,
  isCodeOnlyTitle,
  type GenerationErrorClass,
  type NotificationKind,
  type PipelineNotification,
} from "@scandi/shared";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { Translate } from "@/i18n/translate";
import { useMarkNotificationsRead, useNotifications } from "@/hooks/use-pipeline";
import { briefPath } from "@/features/pipeline/brief-path";
import { toneOf, type Status } from "@/features/pipeline/editor/status";
import { deckPath, looseShotPath } from "@/features/storyboard/paths";
import { SafeText } from "@/features/storyboard/safe-text";

/**
 * Each kind's dot: the tone of the status it is news about, or a plain ink for
 * the conversation kinds (and the strategist's yellow for a note, the colour
 * the deck gives it). A solid dot rather than the chip's tint: at 8px a tint is
 * invisible.
 */
const KIND_TONE: Record<NotificationKind, Status | "talk" | "note"> = {
  assigned: "todo",
  changes_requested: "corrections",
  delivered: "review",
  approved: "done",
  mention: "talk",
  overdue_digest: "corrections",
  // The folder arrives with the release's colour: it is the same kind of news
  // - "here is something for you to work with" - and not an alarm.
  folder_assigned: "todo",
  review_requested: "strategist",
  pending_review: "admin",
  auto_approved: "admin",
  comment: "talk",
  note_added: "note",
  // A clip that finished, or failed, after its requester left the popup.
  generation_done: "done",
  generation_failed: "corrections",
  // Ideas: a new one waits like a concept in review; an answer is good news or a no.
  idea_submitted: "doing",
  idea_reviewed: "done",
  // Round 3 (WhatsApp): most start off in the role matrix; when on, they ring here too.
  potential_winner: "done",
  unassigned: "trash",
  concept_withdrawn: "trash",
  started: "doing",
  resubmitted: "review",
  sent_back: "corrections",
  shipped: "done",
  cancelled: "trash",
  spend_warning: "corrections",
  spend_reached: "corrections",
  generation_alert: "corrections",
  sync_failed: "corrections",
  whatsapp_failed: "corrections",
  user_signed_up: "talk",
  weekly_results: "talk",
  weekly_pipeline: "talk",
  // The cloud agents: a finished draft is good news; a failure or a budget stop needs someone.
  agent_job_ready: "done",
  agent_job_failed: "corrections",
  agent_budget_reached: "corrections",
  agent_job_published: "done",
  agent_job_unpublished: "talk",
  agent_cost_alert: "corrections",
};

const KIND_KEY = {
  assigned: "pipeline.notifications.kinds.assigned",
  changes_requested: "pipeline.notifications.kinds.changes_requested",
  delivered: "pipeline.notifications.kinds.delivered",
  approved: "pipeline.notifications.kinds.approved",
  mention: "pipeline.notifications.kinds.mention",
  overdue_digest: "pipeline.notifications.kinds.overdue_digest",
  folder_assigned: "pipeline.notifications.kinds.folder_assigned",
  review_requested: "pipeline.notifications.kinds.review_requested",
  pending_review: "pipeline.notifications.kinds.pending_review",
  auto_approved: "pipeline.notifications.kinds.auto_approved",
  comment: "pipeline.notifications.kinds.comment",
  note_added: "pipeline.notifications.kinds.note_added",
  generation_done: "pipeline.notifications.kinds.generation_done",
  generation_failed: "pipeline.notifications.kinds.generation_failed",
  idea_submitted: "ideas.bell.submitted",
  idea_reviewed: "ideas.bell.approved",
  potential_winner: "notify.bell.potential_winner",
  unassigned: "notify.bell.unassigned",
  concept_withdrawn: "notify.bell.concept_withdrawn",
  started: "notify.bell.started",
  resubmitted: "notify.bell.resubmitted",
  sent_back: "notify.bell.sent_back",
  shipped: "notify.bell.shipped",
  cancelled: "notify.bell.cancelled",
  spend_warning: "notify.bell.spend_warning",
  spend_reached: "notify.bell.spend_reached",
  generation_alert: "notify.bell.generation_alert",
  sync_failed: "notify.bell.sync_failed",
  whatsapp_failed: "notify.bell.whatsapp_failed",
  user_signed_up: "notify.bell.user_signed_up",
  weekly_results: "notify.bell.weekly_results",
  weekly_pipeline: "notify.bell.weekly_pipeline",
  agent_job_ready: "notify.bell.agent_job_ready",
  agent_job_failed: "notify.bell.agent_job_failed",
  agent_budget_reached: "notify.bell.agent_budget_reached",
  agent_job_published: "notify.bell.agent_job_published",
  agent_job_unpublished: "notify.bell.agent_job_unpublished",
  agent_cost_alert: "notify.bell.agent_cost_alert",
} as const satisfies Record<NotificationKind, string>;

/** Where the newer kinds that name no concept go. */
const KIND_PATH: Partial<Record<NotificationKind, string>> = {
  spend_warning: "/admin/gasto",
  spend_reached: "/admin/gasto",
  generation_alert: "/admin/gasto",
  sync_failed: "/settings/data-sync",
  whatsapp_failed: "/settings/notifications?tab=log",
  user_signed_up: "/admin/users",
  weekly_results: "/pipeline/mi-trabajo",
  weekly_pipeline: "/pipeline/conceptos",
  overdue_digest: "/pipeline",
  // The cloud agents' kinds carry their job's own path (see `target`); this is the fallback.
  agent_job_ready: "/estudio/trabajos",
  agent_job_failed: "/estudio/trabajos",
  agent_budget_reached: "/estudio/trabajos",
  agent_job_unpublished: "/estudio/trabajos",
  // The cost ledger's checks are read on the agents' admin page.
  agent_cost_alert: "/admin/agentes",
};

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value.trim() : null);
const positive = (value: unknown): number | null => (typeof value === "number" && value > 0 ? value : null);

/** The sentence for one row. */
function sentence(item: PipelineNotification, t: Translate): string {
  const payload = item.payload ?? {};
  const vars = { name: item.actorName ?? t("pipeline.notifications.someone"), ref: item.briefRef ?? "" };
  if (item.kind === "approved" && payload.autoApproved === true) {
    return t("pipeline.notifications.approvedAutomatically", vars);
  }
  if (item.kind === "review_requested" && text(payload.viaKey)) {
    return t("pipeline.notifications.reviewRequestedByAgent", vars);
  }
  const shot = isClip(item) ? text(payload.shot) : null;
  if (shot) {
    return t(item.kind === "generation_done" ? "generation.clips.bell.done" : "generation.clips.bell.failed", { shot: shot.toUpperCase() });
  }
  if (item.kind === "idea_reviewed") {
    return t(payload.decision === "rejected" ? "ideas.bell.rejected" : "ideas.bell.approved", vars);
  }
  const round = positive(payload.round);
  if (item.kind === "changes_requested" && round !== null) {
    return t("pipeline.notifications.changesRequestedRound", { ...vars, round });
  }
  return t(KIND_KEY[item.kind], {
    ...vars,
    // The newer kinds name a video, a person or a sync rather than a concept.
    video: text(payload.video) ?? "",
    person: text(payload.person) ?? "",
    source: payload.source === "tracker" ? "Tracker" : "Meta",
    // The cloud agents' kinds name a job ("J-42") and its title.
    job: text(payload.job) ?? "",
    title: text(payload.title) ?? "",
  });
}

/**
 * The words the news carries, when it carries any, flattened to one run of
 * text for a two-line preview: bullets become a middle dot, line breaks a
 * space. Bold survives (`SafeText` inline); nothing else is interpreted.
 */
function excerpt(item: PipelineNotification, t: Translate): string | null {
  const payload = item.payload ?? {};
  if (item.kind === "generation_failed") return clipFailure(item, t);
  // An idea: its title, or the reviewer's note on it.
  if (item.kind === "idea_submitted") return text(payload.ideaTitle);
  if (item.kind === "idea_reviewed") return text(payload.note) ?? text(payload.ideaTitle);
  const raw =
    item.kind === "changes_requested"
      ? text(payload.note)
      : item.kind === "comment" || item.kind === "mention" || item.kind === "note_added"
        ? text(payload.excerpt)
        : null;
  if (raw === null) return null;
  return raw
    .replace(/^\s*[-*\u2022]\s+/, "")
    .replace(/\n\s*[-*\u2022]\s+/g, " \u00b7 ")
    .replace(/\s*\n\s*/g, " ");
}

/** How long ago, with "just now" for the first minute rather than "0 min ago". */
function ago(at: string, t: Translate): string {
  const ms = Date.now() - new Date(at).getTime();
  return ms < 60_000 ? t("common.justNow") : formatRelativeTime(ms);
}

/** A clip's bell row (plan §2.11: frames and voiceovers ring none). */
const isClip = (item: PipelineNotification): boolean =>
  (item.kind === "generation_done" || item.kind === "generation_failed") && item.payload?.kind === "clip";

/** Why a clip failed, in the words its popup uses (the provider's own text stays in the popup's Details). */
function clipFailure(item: PipelineNotification, t: Translate): string | null {
  const cls = item.payload?.errorClass;
  if (typeof cls !== "string" || !(GENERATION_ERROR_CLASSES as readonly string[]).includes(cls)) return null;
  const provider = t("generation.kit.providers.higgsfield");
  switch (cls as GenerationErrorClass) {
    case "safety":
      return t("generation.state.safety");
    case "no_image":
      return t("generation.state.no_image");
    case "bad_request":
      return t("generation.state.bad_request_plain", { provider });
    case "bad_reference":
      return t("generation.state.bad_reference_plain");
    case "credits":
      return t("generation.state.credits", { provider });
    case "voice":
      return t("generation.state.voice");
    case "provider_down":
      return t("generation.state.provider_down", { provider });
    case "timeout":
      return t("generation.state.timeout");
    case "crashed":
      return t("generation.state.crashed");
    default:
      return null;
  }
}

/**
 * Where the row goes: the concept; for a note, the deck the editor reads it
 * in; for a clip, its shot's edit mode (found by its key wherever it sits, so the
 * line segment is left for the page to put right).
 */
function target(item: PipelineNotification): string {
  if ((item.kind === "idea_submitted" || item.kind === "idea_reviewed") && positive(item.payload?.ideaId)) {
    return `/ideas/${item.payload!.ideaId as number}`;
  }
  // The cloud agents' news goes to its job (or chat) in the studio, whose path the payload carries.
  const studio = text(item.payload?.path);
  if (studio && studio.startsWith("/estudio/")) return studio;
  // The concept is out of the editor's sight now: their work page, not a 404.
  if (item.kind === "concept_withdrawn") return "/pipeline/mi-trabajo";
  if (item.kind === "potential_winner" && text(item.payload?.slug)) return `/creatives/${encodeURIComponent(text(item.payload?.slug)!)}`;
  const fixed = KIND_PATH[item.kind];
  if (fixed && !item.briefRef) return fixed;
  if (!item.briefRef) return "/pipeline";
  const shot = isClip(item) ? text(item.payload?.shot) : null;
  if (shot) return looseShotPath(item.briefRef, shot);
  return item.kind === "note_added" ? deckPath(item.briefRef) : briefPath(item.briefRef);
}

export function NotificationsBell() {
  const { t } = useTranslation();
  const query = useNotifications();
  const { markRead } = useMarkNotificationsRead();

  const unread = query.data?.unread ?? 0;
  const items = query.data?.items ?? [];
  // Opening marks everything read at once, so what was new is remembered for
  // as long as the menu stays open: otherwise the highlight vanishes the moment
  // the reader arrives to look at it.
  const [fresh, setFresh] = useState<ReadonlySet<number>>(new Set());

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) return;
        setFresh(new Set(items.filter((item) => item.readAt === null).map((item) => item.id)));
        if (unread > 0) markRead();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={
            unread > 0 ? t("pipeline.notifications.unread", { count: unread }) : t("pipeline.notifications.title")
          }
        >
          <BellIcon className="size-4" />
          {unread > 0 && (
            <span className="bg-brand text-brand-foreground absolute -top-0.5 -end-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[22rem] max-w-[calc(100vw-1.5rem)]">
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          {t("pipeline.notifications.title")}
          {unread > 0 && (
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-[11px]"
              onClick={() => markRead()}
            >
              <CheckCheckIcon className="size-3" aria-hidden />
              {t("pipeline.notifications.markAll")}
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {query.isLoading ? (
          <div className="flex flex-col gap-2 p-2">
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} className="h-11 w-full rounded-lg" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground px-2 py-6 text-center text-xs">{t("pipeline.notifications.empty")}</p>
        ) : (
          <div className="max-h-[26rem] overflow-y-auto overscroll-contain">
            {items.map((item) => {
              // An idea's answer wears its own colour: green approved, red not approved.
              const tone = item.kind === "idea_reviewed" && item.payload?.decision === "rejected" ? "corrections" : KIND_TONE[item.kind];
              const words = excerpt(item, t);
              const dueOn = item.kind === "assigned" ? text(item.payload?.dueOn) : null;
              const read = !fresh.has(item.id) && item.readAt !== null;
              return (
                <DropdownMenuItem
                  key={item.id}
                  asChild
                  className={cn("items-start gap-2.5 py-2", fresh.has(item.id) && "bg-brand/5")}
                >
                  <Link to={target(item)}>
                    <span
                      {...(tone === "talk" || tone === "note" ? {} : toneOf(tone))}
                      className={cn(
                        "mt-1 size-2 shrink-0 rounded-full",
                        tone === "talk" ? "bg-muted-foreground" : tone === "note" ? "bg-warning" : "bg-tone",
                        read && "opacity-35",
                      )}
                      aria-hidden
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="text-xs leading-snug break-words">{sentence(item, t)}</span>
                      {words && (
                        <SafeText
                          inline
                          text={words}
                          className="text-foreground/70 border-border line-clamp-2 border-s-2 ps-2 text-[11.5px] leading-snug break-words"
                        />
                      )}
                      <span className="text-muted-foreground truncate text-[11px]">
                        {[
                          item.briefTitle && !isCodeOnlyTitle(item.briefTitle) ? item.briefTitle : null,
                          dueOn ? t("pipeline.notifications.due", { date: formatShortDate(dueOn) }) : null,
                          isClip(item) && item.payload?.mode !== "live" ? t("generation.words.testRender") : null,
                          ago(item.createdAt, t),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
